// archivo: src/app/jerarquia.service.ts
import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { ConfigService } from './config.service';
import { Entity } from '../models/entity.model';
import { User } from '../models/user.model';
import * as utils from 'src/app/shared/helpers/utils';
import { UserService } from './user.service';
import { LoginService } from './login.service';
import { EntityService } from './entity.service';

@Injectable({
  providedIn: 'root'
})
export class TestsService {

  user: User = utils.localStorageGetItem('user');

  constructor(
    private configSvc: ConfigService,
    private configService: ConfigService,
    private entityService: EntityService
  ) { }

  obtenerYJerarquizarJson(_typeData: string, _customTreeNode?: Entity[], _entityType?: number): Observable<any> {
    let parentEntities: Entity[] = [];
    switch (_typeData) {
      case 'analyticPackages':
        parentEntities = this.configSvc.currentConfigValue.childEntities?.filter((entity: Entity) => entity.entityTypeId !== 4)!;
        return of(this.crearJerarquia(parentEntities, true, _typeData, _entityType));
      case 'locatedList':
        if (this.configService.currentConfigValue.user?.roleId === 2) {
          parentEntities = this.configSvc.currentConfigValue.childEntities?.filter((entity: Entity) => entity.entityTypeId !== 4)!;
          parentEntities.push(this.configSvc.currentConfigValue.userEntity!);
        } else {
          parentEntities = this.configSvc.currentConfigValue.childEntities?.filter((entity: Entity) => entity.entityTypeId !== 4)!;
        }
        return of(this.crearJerarquia(parentEntities, true, _typeData, _entityType));
      case 'located':
        return of(this.crearJerarquia(_customTreeNode!, true, _typeData, _entityType));
      case 'policy':
      case 'newTeam':
        return of(this.crearJerarquia(_customTreeNode!, true, _typeData));
      case 'reassignPackage':
        // Callers may narrow the destinations (e.g. only what hangs below the package's entity).
        // Base entities are never a destination here, only an origin, so they are left out.
        return of(this.crearJerarquia(
          (_customTreeNode ?? this.configSvc.currentConfigValue.childEntities!).filter((e: Entity) => !e.isBase),
          true
        ));
      case 'reassignNoDevice':
        return of(this.crearJerarquia(_customTreeNode!, true));
      case 'newPackage':
        return of(this.crearJerarquia(this.configSvc.currentConfigValue.childEntities!, true));
      case 'package':
        return of(this.crearJerarquia(
          this.configSvc.currentConfigValue.childEntities!.filter((e: Entity) => !e.isBase),
          true
        ));
      case 'policiesList':
        return of(this.crearJerarquia(
          this.configSvc.currentConfigValue.childEntities!.filter((e: Entity) => !e.isBase),
          true,
          _typeData
        ));

      default:
        return of(this.crearJerarquia(
          this.configSvc.currentConfigValue.childEntities!.filter((e: Entity) => !e.isBase),
          true
        ));
    }
  }
  
searchFiltering(_search: string, _typeData: string): Observable<any> {
  // Package creation is the one place base entities are a valid target, and by now the shared slot
  // may have been written by any other tree (its own build persists whatever list it was given, and
  // the very first one runs before typeData is bound). Take that list from its real source instead,
  // the same one the newPackage tree is built from, so the result does not depend on who wrote last.
  const allEntities: Entity[] = (_typeData === 'newPackage')
    ? this.configSvc.currentConfigValue.childEntities!
    : this.configSvc.currentConfigValue.entities;
  // Only package creation targets base entities; everywhere else they are filtered out
  const entities: Entity[] = (_typeData === 'newPackage')
    ? allEntities
    : allEntities.filter((e: Entity) => !e.isBase);

  if (!_search.length) {
    return of(this.crearJerarquia(entities, false, _typeData));
  }

  const searchLower = _search.toLowerCase();

  // Build a dictionary id → entity for instant lookups
  const entityMap = new Map<any, Entity>(entities.map((e: Entity) => [e.id, e]));

  // Save the ids of all entities that match the search
  const matchingIds = new Set<any>(
    entities
      .filter((e: Entity) => e.name.toLowerCase().includes(searchLower))
      .map((e: Entity) => e.id)
  );
  // Go over all entities and include those whose name, parent or grandparent matches
  const filteredEntities = entities.filter((item: Entity) => {
    // The entity itself matches
    if (matchingIds.has(item.id)) return true;

    // The parent matches
    const parent = item.parentEntityId != null ? entityMap.get(item.parentEntityId) : null;
    if (parent && matchingIds.has(parent.id)) return true;

    // The grandparent matches
    const grandparent = parent?.parentEntityId != null ? entityMap.get(parent.parentEntityId) : null;
    if (grandparent && matchingIds.has(grandparent.id)) return true;

    return false;
  });

  return of(this.crearJerarquia(filteredEntities.map((e: Entity) => ({ ...e })), false, _typeData));
}

  // Building a tree publishes the list it was built from into one shared slot that every selector's
  // search reads. A caller that narrowed that list has to put the unnarrowed one back when it is done,
  // or the pages it leaves behind keep searching over its few entities. Same list the plain cases
  // publish, so it restores what those selectors expect.
  public republishAllEntities(): void {
    const childEntities: Entity[] = this.configSvc.currentConfigValue.childEntities || [];
    this.crearJerarquia(childEntities.filter((e: Entity) => !e.isBase), true);
  }

  // Método para jerarquizar los datos
  public crearJerarquia(data: any[], _persist: boolean, _typeData?: string, _entityType?: number): any[] {
    if (_persist) this.configSvc.setConfigParamsValue('entities', data);
    let elementos: { [key: number]: any } = {};
    let jerarquia: any[] = [];

    // Primero, indexamos todos los elementos por su id
    data.forEach(item => {
      elementos[item.id] = item;
      item.children = []; // Inicializamos la propiedad children
    });

    // Ahora, construimos la jerarquía
    data = data.sort((a, b) => a.name.localeCompare(b.name));
    data.forEach(item => {
      let parentId = item.parentEntityId;
      if (parentId !== null && parentId !== undefined) {
        // Si tiene un parentEntityId, lo agregamos como hijo del elemento padre
        if (elementos[parentId]) {
          /*if(item.entityTypeId===3){
            item.level= 1;
          }else{
            item.level= 2;
          } 
          item.expandable= true;*/
          if (item.entityTypeId >= 2) {
            item.parentEntity = { name: elementos[parentId].name, entityTypeId: elementos[parentId].entityTypeId }
          }
          if (elementos[elementos[parentId].parentEntityId]) item.level = 2
          else item.level = 1;
          switch (_typeData) {
            case 'analyticPackages':
            case 'locatedList':
            case 'located':
              switch (_entityType) {
                case 3:
                  break;
                default:
                  elementos[parentId].children.push(item);
                  elementos[parentId].expandable = true;
                  break;
              }
              break;
            case 'policy':
            case 'newTeam':
            case 'policiesList':
              break;
            default:
              elementos[parentId].children.push(item);
              elementos[parentId].expandable = true;
              break;
          }

        } else {
          /*item.level= 0;
          item.expandable= true;*/
          switch (_typeData) {
            case 'policy':
            case 'newTeam':
            case 'policiesList':
              if (item.entityTypeId === 2) {
                item.level = 0;
                jerarquia.push(item);
              }
              break;
            default:
              item.level = 0;
              jerarquia.push(item);
              break;
          }
        }
      } else {
        // Si no tiene parentEntityId, lo agregamos a la jerarquía principal
        item.level = 0;
        jerarquia.push(item);
      }
    });

    return jerarquia;
  }
}

